import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppBrowserSettings } from "./AppBrowserSettings";
import { AppTrAnswer, AppTrSettings, AppTranslate } from "./AppTranslate";
import { AppTrBook, AppTrChat, AppTranslateRules, IAppTrMemory } from "./AppTranslateRules";
import { AppSignUpApi } from "./AppSignUpApi";
import { AppLivePost } from "./AppLiveFeed";

// AppTranslator - the page's `/translate` conversation for the whole visit: `get` (what the chip shows, and whether
// a send carries `translate: true`), `set` (the chip panel's Save, the suggestion's one tap) and `earlier` ("Translate
// earlier messages"). The rules are `AppTranslateRules`/`AppTrBook`; the wire is `AppTranslate`.
//
// ⭐ ONE `get` IN FLIGHT PER CHAT: the chip and a send waiting for the chip's state share it (`Waiting`).
// ⛔ A LOST CONNECTION IS NOT AN ANSWER (`AppTrBook.Take`) - the chip stays hidden and a waiting send is refused
//    with a sentence, never written untranslated.
// ⛔ NOTHING HERE SENDS A MESSAGE - it only asks and saves settings. The send is `AppComposer.SendText`'s.
// ⚠ SHAPED BY AN INTERFACE (`IAppTrPost`) so the suite answers the transport by hand.

/** One POST, answered once with (status, text). */
export interface IAppTrPost {
    Post(Url: string, Body: string, Done: (Status: number, Text: string) => void): void;
}

/** Hears one chat's state once `get` answered (or could not). */
export interface IAppTrHeard {
    Heard(Href: string, Chat: AppTrChat): void;
}

/** Hears a `set`: `Why` "" = saved (the book holds the new settings), else the sentence to show. */
export interface IAppTrSaved {
    Saved(Href: string, Why: string): void;
}

/** Hears an `earlier`: the answer read, or `Lost` (the service was not reached). */
export interface IAppTrEarlier {
    Earlier(Href: string, Answer: AppTrAnswer, Lost: boolean): void;
}

/** The browser's transport: a fresh `AppLivePost` per request (no client timeout - the service answers). */
export class AppTranslateBrowserPost implements IAppTrPost {
    public Post = (Url: string, Body: string, Done: (Status: number, Text: string) => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            new AppLivePost().Post(Url, Body, Done);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateBrowserPost.Post");
        }
    };
}

/** Runs one callback once, after a while - how a chip asks again after a `retry` refusal (the suite runs it by hand). */
export interface IAppTrLater {
    After(Ms: number, Then: () => void): void;
}

/** Never runs anything - the default where nothing should be asked again by itself. */
export class AppTrNoLater implements IAppTrLater {
    /** DOCUMENTED EXCEPTION: does nothing on purpose. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public After = (Ms: number, Then: () => void): void => {
        return;
    };
}

/** The browser's: one `window.setTimeout`. */
export class AppTrWindowLater implements IAppTrLater {
    public After = (Ms: number, Then: () => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            window.setTimeout(Then, Math.max(0, Ms));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrWindowLater.After");
        }
    };
}

export class AppTranslator {
    public Book: AppTrBook = new AppTrBook();
    /** ⭐ How a chip asks again after a refusal that clears on its own (`AppTrChat.RetrySec`). */
    public Later: IAppTrLater = new AppTrNoLater();
    private Post: IAppTrPost;
    private ServiceUrl: string = "";
    private Phone: string = "";
    private Key: string = "";
    private Waiting: Record<string, IAppTrHeard[]> = {};

    public constructor(Post: IAppTrPost, Memory: IAppTrMemory | null = null, Later: IAppTrLater | null = null) {
        this.Post = Post;
        if (Memory !== null) { this.Book.Memory = Memory; }
        if (Later !== null) { this.Later = Later; }
    }

    /** Who asks - set before every contact paint (a sign-in can change it). */
    public Who = (ServiceUrl: string, Phone: string, Key: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ServiceUrl = nz(ServiceUrl);
            this.Phone = nz(Phone);
            this.Key = nz(Key);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.Who");
        }
    };

    /** Can this page ask at all (the hosted page, with a key)? */
    public CanAsk = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.ServiceUrl.length > 0 && this.Key.length > 0 && this.Phone.length > 0 && this.Book.Off === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.CanAsk");
            return false;
        }
    };

    /**
     * ⭐ DOES THIS SEND CARRY `translate: true`? "yes" / "no" / "ask". `Asked` is the owner's own choice on a failed
     * bubble ("yes" = Retry, "no" = Send in English) and wins; otherwise a page that cannot ask (no service, or a
     * service without the route) sends as typed, and a chat not yet known is "ask" (`AppTrBook.Wants`).
     */
    public ModeFor = (Href: string, Asked: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "ask"; }
        try {
            if (Asked === "yes" || Asked === "no") { return Asked; }
            if (this.CanAsk() === false) { return "no"; }
            return this.Book.Wants(Href);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.ModeFor");
            return "ask";
        }
    };

    /**
     * What this chat's translation state is. Answered at once when the visit already knows it (or knows the route is
     * off), else after one `get` - shared with anyone else asking about the same chat meanwhile.
     */
    public Ask = (Href: string, Then: IAppTrHeard): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Chat: AppTrChat = this.Book.Of(Href);
            if (this.CanAsk() === false || Chat.Known === true) { Then.Heard(Href, Chat); return; }
            if (Object.prototype.hasOwnProperty.call(this.Waiting, Href) === true) { this.Waiting[Href].push(Then); return; }
            this.Waiting[Href] = [Then];
            this.Send(Href, "get", null, "", new AppTrReply(this, Href, "get", null));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.Ask");
        }
    };

    /** Save this chat's settings (the panel's Save, or the suggestion's tap). ⛔ Only ever from a human act. */
    public Save = (Href: string, Settings: AppTrSettings, Then: IAppTrSaved): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.CanAsk() === false) { Then.Saved(Href, AppTranslateRules.SaveLostWhy); return; }
            this.Send(Href, "set", Settings, "", new AppTrReply(this, Href, "set", Then));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.Save");
        }
    };

    /** Translate the next batch of older messages ("Translate earlier messages"). */
    public Earlier = (Href: string, Before: string, Then: IAppTrEarlier): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.CanAsk() === false) { Then.Earlier(Href, new AppTrAnswer(), true); return; }
            this.Send(Href, "earlier", null, Before, new AppTrReply(this, Href, "earlier", Then));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.Earlier");
        }
    };

    private Send = (Href: string, Action: string, Settings: AppTrSettings | null, Before: string, Reply: AppTrReply): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Url: string = AppSignUpApi.UrlFor(this.ServiceUrl, AppTranslate.Route);
            const Body: string = AppTranslate.BodyFor(this.Phone, this.Key, Href, Action, Settings, Before);
            if (Url.length === 0 || Body.length === 0) { Reply.OnAnswer(0, ""); return; }
            this.Post.Post(Url, Body, Reply.OnAnswer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.Send");
        }
    };

    /** `get` answered: the book reads it, every waiter hears it (once). */
    public OnGet = (Href: string, Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Chat: AppTrChat = this.Book.Take(Href, Status, Text);
            const Waiters: IAppTrHeard[] = this.Waiting[Href] || [];
            delete this.Waiting[Href];
            for (const One of Waiters) { One.Heard(Href, Chat); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.OnGet");
        }
    };

    /** `set` answered. */
    public OnSet = (Href: string, Status: number, Text: string, Then: IAppTrSaved): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Status === 0 || Status >= 500) { Then.Saved(Href, AppTranslateRules.SaveLostWhy); return; }
            const Answer: AppTrAnswer = AppTranslate.Parse(Text);
            if (Answer.Off === true) { Then.Saved(Href, AppTranslateRules.SaveLostWhy); return; }
            if (Answer.Ok !== true || Answer.Saved !== true) {
                let Why: string = Answer.Why;
                if (Why.length === 0) { Why = AppTranslateRules.SaveLostWhy; }
                Then.Saved(Href, Why);
                return;
            }
            this.Book.Take(Href, Status, Text);
            Then.Saved(Href, "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.OnSet");
        }
    };

    /** `earlier` answered. */
    public OnEarlier = (Href: string, Status: number, Text: string, Then: IAppTrEarlier): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Status === 0 || Status >= 500) { Then.Earlier(Href, new AppTrAnswer(), true); return; }
            const Answer: AppTrAnswer = AppTranslate.Parse(Text);
            if (Answer.Capped === true) { this.Book.Of(Href).Capped = true; }
            Then.Earlier(Href, Answer, Answer.Off === true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslator.OnEarlier");
        }
    };
}

/** One `/translate` request's answer, carried to its handler. A class, not a closure. */
export class AppTrReply {
    private Owner: AppTranslator;
    private Href: string;
    private Action: string;
    private Then: IAppTrSaved | IAppTrEarlier | null;
    private Done: boolean = false;

    public constructor(Owner: AppTranslator, Href: string, Action: string, Then: IAppTrSaved | IAppTrEarlier | null) {
        this.Owner = Owner;
        this.Href = Href;
        this.Action = Action;
        this.Then = Then;
    }

    /** ⛔ ANSWERED ONCE - a second delivery is dropped. ERROR-SUPPRESSED-JUSTIFIED: no gate - waiters depend on it. */
    public OnAnswer = (Status: number, Text: string): void => {
        try {
            if (this.Done === true) { return; }
            this.Done = true;
            if (this.Action === "get") { this.Owner.OnGet(this.Href, Status, Text); return; }
            if (this.Action === "set") { this.Owner.OnSet(this.Href, Status, Text, this.Then as IAppTrSaved); return; }
            this.Owner.OnEarlier(this.Href, Status, Text, this.Then as IAppTrEarlier);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrReply.OnAnswer");
        }
    };
}

/**
 * The page's memory of which chats were last seen with translation ON, in `localStorage` (one key, a list of FNV
 * hashes of the page hrefs - never a name). ⚠ Every access is wrapped: storage throws in private browsing and at
 * quota; then this visit's memory stands in (a lost answer then sends as typed, as for any unknown chat).
 */
export class AppTrLocalMemory implements IAppTrMemory {
    public static readonly Key: string = "wac.translate.on";
    public static readonly Max: number = 500;
    private Visit: Record<string, boolean> = {};

    public WasOn = (Href: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Id: string = AppTrLocalMemory.Hash(Href);
            if (Object.prototype.hasOwnProperty.call(this.Visit, Id) === true) { return this.Visit[Id]; }
            return AppTrLocalMemory.Read().indexOf(Id) >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrLocalMemory.WasOn");
            return false;
        }
    };

    public Note = (Href: string, On: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Id: string = AppTrLocalMemory.Hash(Href);
            this.Visit[Id] = On;
            const List: string[] = AppTrLocalMemory.Read().filter(new AppTrNotId(Id).Keep);
            if (On === true) { List.push(Id); }
            AppTrLocalMemory.Write(List.slice(-AppTrLocalMemory.Max));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrLocalMemory.Note");
        }
    };

    /** FNV-1a of the href, hex. DOCUMENTED EXCEPTION: pure arithmetic. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Hash = (Href: string): string => {
        const Text: string = nz(Href);
        let H: number = 0x811c9dc5;
        for (let At: number = 0; At < Text.length; At = At + 1) { H = Math.imul(H ^ Text.charCodeAt(At), 0x01000193) >>> 0; }
        return ("00000000" + H.toString(16)).slice(-8);
    };

    /** Storage that throws or holds nonsense reads as "none remembered" (this visit's memory stands in). */
    private static Read = (): string[] => {
        try {
            // Blocked or missing storage, an empty text and damaged JSON all come back as the shared class's fallback (none remembered).
            const Raw: unknown = AppBrowserSettings.GlobalLocal().ReadJson<unknown>(AppTrLocalMemory.Key, []).Value;
            if (Array.isArray(Raw) === false) { return []; }
            return (Raw as unknown[]).filter(AppTrLocalMemory.IsId) as string[];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrLocalMemory.Read");
            return [];
        }
    };

    /** A refused write (private mode, quota) is answered by the shared class as a plain outcome: this visit's memory (`Visit`) is all that stays. */
    private static Write = (List: string[]): void => {
        try {
            AppBrowserSettings.GlobalLocal().WriteJson(AppTrLocalMemory.Key, List);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrLocalMemory.Write");
        }
    };

    /** DOCUMENTED EXCEPTION: a shape check for `filter`. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    private static IsId = (Value: unknown): boolean => {
        return typeof Value === "string" && /^[0-9a-f]{8}$/.test(Value);
    };
}

/** `filter` predicate: every id but one. A class, not a closure. */
export class AppTrNotId {
    private Id: string;
    public constructor(Id: string) { this.Id = Id; }
    /** DOCUMENTED EXCEPTION: a compare. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public Keep = (Value: string): boolean => { return Value !== this.Id; };
}
